import { useRef, useEffect, useState } from "react";
import {
  AMapComponent,
  type AMapComponentRef,
  type RouteInfo,
} from "./AmapComponets";

declare global {
  interface Window {
    AMap: any;
  }
}

interface TripMapPreviewProps {
  className?: string;
  style?: React.CSSProperties;
  startPoint?: {
    lng: number;
    lat: number;
  };
  endPoint?: {
    lng: number;
    lat: number;
  };
  routePath?: Array<{
    lng: number;
    lat: number;
  }>;
  onRouteInfoChange?: (info: RouteInfo | null) => void;
}

export const TripMapPreview = ({
  className,
  style,
  startPoint,
  endPoint,
  routePath = [],
  onRouteInfoChange,
}: TripMapPreviewProps) => {
  const mapRef = useRef<AMapComponentRef>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return;

    mapRef.current.clearStartEndMarkers();
    mapRef.current.clearMiddleMarkers();
    mapRef.current.clearRoute();

    if (startPoint) {
      mapRef.current.addStartMarker(startPoint.lng, startPoint.lat);
    }

    if (endPoint) {
      mapRef.current.addEndMarker(endPoint.lng, endPoint.lat);
    }

    if (routePath.length === 1 && startPoint) {
      onRouteInfoChange?.(null);
      mapRef.current.focusPoint(startPoint.lng, startPoint.lat, 15);
      return;
    }

    if (startPoint && endPoint) {
      const waypoints = routePath
        .slice(1, Math.max(routePath.length - 1, 1))
        .map((point) => [point.lng, point.lat] as [number, number]);

      waypoints.forEach(([lng, lat]) => {
        mapRef.current?.addMiddleMarker(lng, lat);
      });

      mapRef.current.planRoute(
        [startPoint.lng, startPoint.lat],
        [endPoint.lng, endPoint.lat],
        waypoints,
      );
    }
  }, [isMapReady, startPoint, endPoint, routePath, onRouteInfoChange]);

  const handleMapReady = () => {
    setIsMapReady(true);
  };

  return (
    <div className={`relative w-full h-full ${className || ""}`} style={style}>
      <AMapComponent
        ref={mapRef}
        onLoad={handleMapReady}
        onRouteInfoChange={onRouteInfoChange}
      />
    </div>
  );
};

export default TripMapPreview;
